import React, { useMemo, useRef, useCallback } from "react";
import { BarRounded } from "@visx/shape";
import { Group } from "@visx/group";
import { GridRows } from "@visx/grid";
import { scaleBand, scaleLinear } from "@visx/scale";
import { TooltipWithBounds, defaultStyles, useTooltip } from "@visx/tooltip";
import { useSpring, useTransition, animated } from "react-spring";
import { AnimatedAxis } from "@visx/react-spring";
import { Box, Flex, Text } from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  backgroundGrey,
  color1,
  color2,
  color3,
  color4,
  color5,
  color6,
  textSecondary,
} from "../../../../utils/colors";
import { useTranslation } from "react-i18next";
import useNumberFormatter from "../../../../hooks/useNumberFormatter";

const defaultMargin = { top: 14, right: 0, bottom: 0, left: 0 };

// Status colors
const statusColors = {
  approved: color3,
  completed: color5,
  cancelled: color6,
  consulting: color1,
  draft: backgroundGrey,
  ongoing: color4,
  planning: color2,
};

const getStatusLabel = (d) => d.status;
const getCount = (d) => d.count;

const StatusBarChart = ({ countsByStatus = {}, width, height }) => {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const formatNumber = useNumberFormatter();

  const allZero = Object.entries(countsByStatus)
    .filter(([key]) => key !== "total") // Exclude the 'total' key
    .every(([_, value]) => value === 0); // Check if all values are zero

  // Tooltip setup
  const {
    hideTooltip,
    showTooltip,
    tooltipData,
    tooltipLeft = 0,
    tooltipOpen,
    tooltipTop = 0,
  } = useTooltip({
    tooltipOpen: false,
  });

  const xMax = width - defaultMargin.left - defaultMargin.right;
  const yMax = height - defaultMargin.top - defaultMargin.bottom;

  // Define the order of statuses
  const statusOrder = [
    "planning",
    "approved",
    "ongoing",
    "completed",
    "cancelled",
  ];

  // Format `countsByStatus` into an array suitable for the chart and sort it based on the defined order
  const statusData = useMemo(() => {
    return Object.keys(countsByStatus)
      .map((status) => ({
        status,
        count: countsByStatus[status],
      }))
      .filter((item) => item.count > 0 && item.status !== "total") // Filter out zero values and 'total' status
      .sort(
        (a, b) => statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status)
      );
  }, [countsByStatus, statusOrder]);

  // Define scales
  const xScale = useMemo(
    () =>
      scaleBand({
        range: [0, xMax],
        round: true,
        domain: statusData.map(getStatusLabel), // Define X axis based on status labels
        padding: 0.3, // Adjust padding for spacing between bars
      }),
    [xMax, statusData]
  );

  const yScale = useMemo(
    () =>
      scaleLinear({
        range: [yMax, 0], // Invert the range for proper scaling in vertical orientation
        nice: true,
        domain: [0, Math.max(...statusData.map(getCount))],
      }).nice(),
    [yMax, statusData]
  );

  // Animate the entire chart's opacity and position on load
  const chartAnimation = useSpring({
    from: { opacity: 0, transform: "translateY(20px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: { duration: 500 },
  });

  // Transition each bar individually using react-spring's useTransition hook
  const transitions = useTransition(statusData, {
    from: { height: 0, opacity: 0 },
    enter: (item) => ({ height: yMax - yScale(item.count), opacity: 1 }),
    update: (item) => ({ height: yMax - yScale(item.count), opacity: 1 }),
    leave: { height: 0, opacity: 0 },
    keys: (item) => item.status, // Key is the status for consistent animation
    config: { tension: 220, friction: 30 }, // Smooth animation config
  });

  const handleTooltipShow = useCallback(
    (e, d) => {
      const { current } = chartRef;
      if (current && d !== undefined) {
        const bounding = current.getBoundingClientRect();
        showTooltip({
          tooltipData: d,
          tooltipLeft: e.clientX - bounding.x,
          tooltipTop: e.clientY - bounding.y,
        });
      }
    },
    [showTooltip]
  );

  const AnimatedBar = animated(BarRounded);

  if (width < 10) return null;

  // Only render the grid when all values are zero
  if (allZero) {
    return (
      <Flex
        w="100%"
        position="relative"
        ref={chartRef}
        direction="column"
        justify="flex-end"
        align="flex-end"
        gap="0.5rem"
        h="100%"
        m="1rem 0 0"
      >
        <Box w="100%" h="1px " borderTop={`1px dashed ${backgroundGrey}`} />
        <Box w="100%" h="1px " borderTop={`1px dashed ${backgroundGrey}`} />
        <Box w="100%" h="1px " borderTop={`1px dashed ${backgroundGrey}`} />
        <Box w="100%" h="1px " borderTop={`1px dashed ${backgroundGrey}`} />
        <Box w="100%" h="1px " borderTop={`1px dashed ${backgroundGrey}`} />
        <Box w="100%" h="1px " borderTop={`1px dashed ${backgroundGrey}`} />
        <Box w="100%" h="1px " borderTop={`1px dashed ${backgroundGrey}`} />
        <Box w="100%" h="1px " borderTop={`1px dashed ${backgroundGrey}`} />
      </Flex>
    );
  }

  return (
    <animated.div style={chartAnimation}>
      <Flex w="100%" position="relative" ref={chartRef} as={motion.div}>
        <svg width={width} height={height}>
          <GridRows
            scale={yScale}
            width={xMax}
            strokeDasharray="6"
            stroke={backgroundGrey}
            top={defaultMargin.top}
            left={defaultMargin.left}
          />

          {/* <Group top={defaultMargin.top} left={defaultMargin.left}>
            {transitions((props, d) => (
              <AnimatedBar
                key={d.status}
                x={xScale(d.status)} // Use xScale to determine X position
                y={yScale(d.count)} // Use yScale to determine Y position
                width={xScale.bandwidth()} // Bar width is determined by xScale's bandwidth
                height={props.height} // Bar height is determined by transitions
                fill={statusColors[d.status]} // Use predefined status colors
                opacity={props.opacity} // Use spring's opacity value
                radius={8}
                top
                bottom
                onMouseMove={(e) => handleTooltipShow(e, d)}
                onMouseLeave={() => hideTooltip()}
              />
            ))}
          </Group> */}

          <Group top={defaultMargin.top} left={defaultMargin.left}>
            {transitions((props, d) => {
              return (
                <>
                  <AnimatedBar
                    key={d.status}
                    x={xScale(d.status)}
                    y={yMax - (yMax - yScale(d.count))} // Adjust y position for bottom-up effect
                    width={xScale.bandwidth()}
                    height={yMax - yScale(d.count)} // Sets height relative to yMax
                    fill={statusColors[d.status]} // Keeps color
                    opacity={props.opacity}
                    radius={8}
                    top
                    bottom
                    onMouseMove={(e) => handleTooltipShow(e, d)} // Attach tooltip events here
                    onMouseLeave={() => hideTooltip()}
                  />
                  <text
                    x={xScale(d.status) + xScale.bandwidth() / 2} // Center the text on the bar
                    y={yScale(d.count) - 5} // Position the text slightly above the bar
                    textAnchor="middle"
                    fontSize={11}
                    fill={textSecondary} // Adjust color as needed
                    fontWeight="500"
                    onMouseMove={(e) => handleTooltipShow(e, d)} // Attach tooltip events here
                    onMouseLeave={() => hideTooltip()}
                    style={{ cursor: "default" }}
                  >
                    {/* {d.count} */}
                    {formatNumber(d.count)} {/* Use formatNumber here */}
                  </text>
                </>
              );
            })}
          </Group>

          {/* X Axis */}
          <AnimatedAxis
            scale={xScale}
            top={yMax + defaultMargin.top}
            left={defaultMargin.left}
            stroke={backgroundGrey}
            orientation="bottom"
            hideTicks
            hideAxisLine
            hideZero
          />

          {/* Y Axis */}
          <AnimatedAxis
            top={defaultMargin.top}
            left={defaultMargin.left}
            scale={yScale}
            orientation="left"
            stroke="#C6D4DB"
            hideTicks
            numTicks={0}
            hideAxisLine
            hideZero
            // tickLabelProps={() => ({
            //   fill: textSecondary,
            //   dx: "5px",
            //   dy: "0px",
            //   fontSize: "10px",
            // })}
          />
        </svg>

        {tooltipData && tooltipOpen && (
          <TooltipWithBounds
            left={tooltipLeft}
            top={tooltipTop}
            style={{
              ...defaultStyles,
              boxShadow: "0px 2px 5px rgba(27, 47, 55, 0.2)",
              padding: "0.5rem 1rem",
              borderRadius: "1rem",
              backgroundColor: "#2D3748",
              color: "white",
              fontSize: "0.8rem",
              fontWeight: "500",
              whiteSpace: "nowrap",
            }}
          >
            <Flex align="center">
              <Flex
                w="6px"
                h="6px"
                borderRadius="50%"
                bg={statusColors[tooltipData.status]}
                mr={1.5}
              />
              <Text fontSize="0.8rem" fontWeight="500" mr={1}>
                {t(`status.${tooltipData.status}`)} -
              </Text>
              <Text fontWeight="600" mr={1}>
                {formatNumber(tooltipData.count)}
              </Text>
              <Text fontWeight="500" textTransform="lowercase">
                {" "}
                {t("treatmentPlan.treatmentPlans")}
              </Text>
            </Flex>
          </TooltipWithBounds>
        )}
      </Flex>
    </animated.div>
  );
};

export default StatusBarChart;
